import { create } from 'zustand';
import { EventService } from '../services/eventComments.service';
import { EventComment } from '../types/event.types';

interface EventsStoreState {
  eventComments: Record<string, EventComment[]>; // eventId -> comments
  addComment: (eventId: string, content: string, parentId?: string) => Promise<EventComment | null>;
  toggleCommentUpvote: (eventId: string, commentId: string) => Promise<{ success: boolean }>;
  deleteComment: (eventId: string, commentId: string) => Promise<{ success: boolean }>;
  setEventComments: (eventId: string, comments: EventComment[]) => void;
  // ...other event related actions
}

export const useEventsStore = create<EventsStoreState>((set, get) => ({
  eventComments: {},

  setEventComments(eventId, comments) {
    set((state) => ({
      eventComments: {
        ...state.eventComments,
        [eventId]: comments,
      },
    }));
  },

  async addComment(eventId, content, parentId) {
    const newComment = await EventService.addComment(eventId, content, parentId);
    if (newComment) {
      function findParentAndAddReply(comments, targetParentId, newComment) {
        return comments.map((c) => {
          if (c.id === targetParentId) {
            return {
              ...c,
              replies: [...(c.replies || []), newComment],
            };
          }
          if (c.replies && c.replies.length > 0) {
            return {
              ...c,
              replies: findParentAndAddReply(c.replies, targetParentId, newComment),
            };
          }
          return c;
        });
      }
      set((state) => {
        const comments = state.eventComments[eventId] || [];
        return {
          eventComments: {
            ...state.eventComments,
            [eventId]: parentId
              ? findParentAndAddReply(comments, parentId, newComment)
              : [...comments, newComment],
          },
        };
      });
    }
    return newComment;
  },

  async toggleCommentUpvote(eventId, commentId) {
    try {
      const result = await EventService.toggleCommentUpvote(eventId, commentId);
      return { success: !!result };
    } catch (e) {
      return { success: false };
    }
  },

  async deleteComment(eventId, commentId) {
    try {
      await EventService.deleteComment(eventId, commentId);
      
      // Remove the comment from the store
      set((state) => {
        const comments = state.eventComments[eventId] || [];
        
        // Helper function to remove comment or its replies
        const removeComment = (comments: EventComment[], targetId: string): EventComment[] => {
          return comments
            .filter(c => c.id !== targetId)
            .map(c => {
              if (c.replies && c.replies.length > 0) {
                return {
                  ...c,
                  replies: removeComment(c.replies, targetId)
                };
              }
              return c;
            });
        };
        
        return {
          eventComments: {
            ...state.eventComments,
            [eventId]: removeComment(comments, commentId)
          }
        };
      });
      
      return { success: true };
    } catch (e) {
      console.error('Error deleting comment:', e);
      return { success: false };
    }
  },
}));
